/**
 * Simple drawdown mini stats — duration + current vs max depth only.
 */

import React, { useMemo } from "react";
import { Box } from "@chakra-ui/react";
import TradeLogMiniStatistics from "../../TradeLog/components/TradeLogMiniStatistics";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import type { RiskMetricsResponse } from "../../../../types/trade";
import {
  computeDrawdownSeriesMetrics,
  formatDayCount,
} from "./drawdownSeriesMetrics";
import { dashboardMiniStatsGridTemplateColumns } from "../../../../utils/miniStatGridLayout";

const TROPICAL_GRADIENT = "linear-gradient(135deg, #38BBD1, #3EEAEC)";
const LOSS_GRADIENT = "linear-gradient(135deg, #E00101, #FE4244)";

export type DrawdownRecoveryMiniStatsProps = {
  drawdownData?: RiskMetricsResponse["drawdown_data"] | null;
  /** Optional API max drawdown for the “current vs max” denominator. */
  maxDrawdown?: number | null;
};

function currentVsMaxAmount(
  metrics: ReturnType<typeof computeDrawdownSeriesMetrics>,
  formatMoney: (value: number) => string,
  maxDrawdownOverride?: number | null
): string {
  if (metrics.currentDrawdown === null) {
    return "N/A";
  }
  const maxFromSeries = metrics.maxDrawdown ?? 0;
  const maxFromApi =
    maxDrawdownOverride !== null && maxDrawdownOverride !== undefined
      ? Math.abs(maxDrawdownOverride)
      : 0;
  const maxDd = Math.max(maxFromSeries, maxFromApi);
  return `${formatMoney(metrics.currentDrawdown)} / ${formatMoney(maxDd)}`;
}

const DrawdownRecoveryMiniStats: React.FC<DrawdownRecoveryMiniStatsProps> = ({
  drawdownData,
  maxDrawdown = null,
}) => {
  const { formatMoney } = useDisplayCurrency();

  const metrics = useMemo(
    () => computeDrawdownSeriesMetrics(drawdownData ?? null),
    [drawdownData]
  );

  const cards = useMemo(
    () => [
      {
        id: "days-in-drawdown",
        title: "Total Days Below High",
        amount: formatDayCount(metrics.daysInDrawdown),
        gradient: TROPICAL_GRADIENT,
        tooltip:
          "How many days equity sat below its best level so far in this period. Every day under a high counts — not just the current dip.",
      },
      {
        id: "longest-drawdown",
        title: "Longest Stretch Below High",
        amount: formatDayCount(metrics.longestDrawdownDays),
        gradient: TROPICAL_GRADIENT,
        tooltip:
          "Longest run of consecutive days equity stayed below its best level so far (from the first day under that high until back at a new or prior best).",
      },
      {
        id: "current-vs-max",
        title: "Current vs Max DD",
        amount: currentVsMaxAmount(metrics, formatMoney, maxDrawdown),
        gradient: (metrics.currentDrawdown ?? 0) > 0 ? LOSS_GRADIENT : TROPICAL_GRADIENT,
        tooltip:
          "How deep equity is under its best level today versus the deepest drop in this period (current / max).",
      },
    ],
    [formatMoney, maxDrawdown, metrics]
  );

  const gridTemplateColumns = useMemo(() => {
    const items = cards.map((card) => ({ title: card.title, amount: card.amount }));
    const mobileTemplate = dashboardMiniStatsGridTemplateColumns(items, {
      isMobile: true,
      maxColumns: 3,
      gapPx: 24,
    });

    if (mobileTemplate === "minmax(0, 1fr)") {
      return {
        base: "minmax(0, 1fr)",
        lg: "repeat(3, minmax(0, 1fr))",
      };
    }

    return {
      base: "repeat(2, minmax(0, 1fr))",
      lg: "repeat(3, minmax(0, 1fr))",
    };
  }, [cards]);

  return (
    <Box
      as="div"
      w="100%"
      minW={0}
      display="grid"
      gridTemplateColumns={gridTemplateColumns}
      gap="24px"
      data-mini-stats-grid
      data-risk-drawdown-recovery-mini-stats=""
    >
      {cards.map((card) => (
        <Box key={card.id} w="100%" minW={0}>
          <TradeLogMiniStatistics
            title={card.title}
            amount={card.amount}
            percentage={null}
            showPercentage={false}
            backgroundGradient={card.gradient}
            tooltip={card.tooltip}
            fitAmount
          />
        </Box>
      ))}
    </Box>
  );
};

export default DrawdownRecoveryMiniStats;
